import React, { useState } from 'react';
import { Container, Typography, Button, Stack, Paper, ButtonBase, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import "../styles/questionnaire.css";
import { useTranslation } from 'react-i18next';


const questions = {
  q1: {
    id: 'q1',
    text: '1. Have you ever had your hearing tested?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q1a' },
      { key: 'no', label: 'No', next: 'q1b' },
      { key: 'unsure', label: 'Unsure', next: 'q2' },
    ],
  },
  q1a: {
    id: 'q1a',
    text: '1a. When did you last have your hearing tested?',
    choices: [
      { key: 'Within-the-past-year', label: 'Within the past year', next: 'q2' },
      { key: '2-5-years', label: '2-5 years ago', next: 'q2' },
      { key: 'within-the-past-5-10-years', label: 'Within the past 5–10 years', next: 'q2' },
      { key: 'more-than-10-years', label: 'More than 10 years ago', next: 'q2' },
    ],
  },
  q1b: {
    id: 'q1b',
    text: '1b. Why not? (Check all that apply)',
    multiple: true,
    nextOnContinue: 'q2',
    choices: [
      { key: 'no-concerns', label: 'I have no concerns about my hearing' },
      { key: 'inconvenient', label: 'I think I might have hearing loss but it’s not bothersome enough to do anything about it right now' },
      { key: 'to-expensive', label: 'I think I might have hearing loss but I don’t have the resources to do anything about it right now' },
      { key: 'no-info', label: 'I think I might have hearing loss but I don’t know where to go for help' },
      { key: 'distrust', label: 'I think I might have hearing loss but I don’t trust the healthcare system' },
    ],
  },
  q2: {
    id: 'q2',
    text: '2. Do you think you might have hearing loss, or have you previously been diagnosed with hearing loss?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q2a' },
      { key: 'no', label: 'No', next: 'q3' },
    ],
  },
  q2a: {
    id: 'q2a',
    text: '2a. I think I might have hearing loss in:',
    choices: [
      { key: 'right', label: 'Right Ear', next: 'q2b' },
      { key: 'left', label: 'Left Ear', next: 'q2b' },
      { key: 'both', label: 'Both Ears', next: 'q2b' },
    ],
  },
  q2b: {
    id: 'q2b',
    text: '2b. I have been diagnosed with hearing loss in:',
    choices: [
      { key: 'right', label: 'Right Ear', next: 'q3' },
      { key: 'left', label: 'Left Ear', next: 'q3' },
      { key: 'both', label: 'Both Ears', next: 'q3' },
    ],
  },
  q3: {
    id: 'q3',
    text: '3. Have you ever been exposed to loud noises (e.g., power tools, firearms, farm equipment, explosions, music concerts)? ',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q3a' },
      { key: 'no', label: 'No', next: 'q4' },
    ],
  },
  q3a: {
    id: 'q3a',
    text: '3a. What type of noise have you been exposed to most often?',
    multiple: true,
    nextOnContinue: 'q3b',
    choices: [
      { key: 'impulse', label: 'Impulse-(e.g., gunshots, fireworks, explosions)', next: 'q3b' },
      { key: 'intermittent', label: 'Intermittent-lasting minutes (nail gun, drill, etc.)', next: 'q3b' },
      { key: 'continuous', label: 'Continuous–lasting hours (tractors, factory noise, music concerts, personal audio devices, etc.)', next: 'q3b' },
    ]
  },
  q3b: {
    id: 'q3b',
    text: '3b. What is the approximate duration of your noise exposure?',
    choices: [
      { key: 'few-instances', label: 'Only a few instances', next: 'q4' },
      { key: 'more-than-few', label: 'More than a few instances', next: 'q4' },
      { key: 'continuous-months', label: 'Prolonged or continuous exposure over months', next: 'q4' },
      { key: 'continuous-years', label: 'Prolonged or continuous exposure over years', next: 'q4' },
    ]
  },
  q4: {
    id: 'q4',
    text: '4. Do you have any ear pain, ear fullness, or drainage?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q5' },
      { key: 'no', label: 'No', next: 'q5' },
    ],
  },
  q5: {
    id: 'q5',
    text: '5. Have you or an immediate family member had ear surgery?',
    multiple: true,
    nextOnContinue: 'q6',
    choices: [
      { key: 'yes-patient', label: 'Yes, me' },
      { key: 'yes-family', label: 'Yes, immediate family member' },
      { key: 'no', label: 'No' },
    ],
  },
  q6: {
    id: 'q6',
    text: '6. Have you ever had any tinnitus (ringing, buzzing, or roaring) in your ears?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q6a' },
      { key: 'no', label: 'No', next: 'q7' },
    ]
  },
  q6a: {
    id: 'q6a',
    text: '6a. Which ear (check all that apply:)',
    multiple: true,
    nextOnContinue: 'q6bl',
    choices: [
      { key: 'left-constant', label: 'Left, Constant', next: 'q6bl' },
      { key: 'left-intermittent', label: 'Left, Intermittent', next: 'q6bl' },
      { key: 'right-constant', label: 'Right, Constant', next: 'q6br' },
      { key: 'right-intermittent', label: 'Right, Intermittent', next: 'q6br' },
    ]
  },
  q6bl: {
    id: 'q6bl',
    text: '6b. How bothersome is your tinnitus in your left ear?',
    choices: [
      { key: '1', label: '1 Not Bothersome', next: 'q6br' },
      { key: '2', label: '2', next: 'q6br' },
      { key: '3', label: '3', next: 'q6br' },
      { key: '4', label: '4', next: 'q6br' },
      { key: '5', label: '5 Extremely Bothersome', next: 'q6br' },
    ]
  },
  q6br: {
    id: 'q6br',
    text: '6b. How bothersome is your tinnitus in your right ear?',
    choices: [
      { key: '1', label: '1 Not Bothersome', next: 'q6c' },
      { key: '2', label: '2', next: 'q6c' },
      { key: '3', label: '3', next: 'q6c' },
      { key: '4', label: '4', next: 'q6c' },
      { key: '5', label: '5 Extremely Bothersome', next: 'q6c' },
    ]
  },
  q6c: {
    id: 'q6c',
    text: '6c. Does the tinnitus sound like your heartbeat?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q7' },
      { key: 'no', label: 'No', next: 'q7' },

    ]
  },

  q7: {
    id: 'q7',
    text: '7. Do you have any of the following diagnoses? (Check all that apply)',
    multiple: true,
    nextOnContinue: 'q8',
    choices: [
      { key: 'hbp', label: 'High blood pressure' },
      { key: 'Diabetes', label: 'Diabetes' },
      { key: 'CHD', label: 'Cardiovascular (heart) disease' },
      { key: 'Stroke', label: 'Stroke' },
      { key: 'D/A', label: 'Dementia / Alzheimer’s' },
      { key: 'Depression', label: 'Depression' },
      { key: 'None', label: 'None of these apply' }
    ]
  },

  q8: {
    id: 'q8',
    text: '8. In the past 6 months, have you experienced dizziness, balance problems, or balance-related falls?',
    choices: [
      { key: 'yes', label: 'Yes', next: 'q8a' },
      { key: 'no', label: 'No', next: null },
    ]
  },

  q8a: {
    id: 'q8a',
    text: '8a. How would you describe your symptoms? (check all that apply):',
    multiple: true,
    choices: [
      { key: 'Spinning', label: 'Spinning', next: null },
      { key: 'Floating', label: 'Floating', next: null },
      { key: 'Lightheaded', label: 'Lightheaded', next: null },
      { key: 'Disorientation', label: 'Disorientation', next: null },
      { key: 'Imbalance', label: 'Off balance or unsteady', next: null },
    ]
  }
};

// Special-case next-question logic that can't be stored in the static data.
// Returns the next question ID, or null to signal completion.
function resolveNext(currentId, choiceNext, answers) {
  // After the q5a multi-select, route to whichever ear was selected first
  if (currentId === 'q6a') {
    const picked = answers.q6a ?? [];
    if (picked.some(k => k.startsWith('left'))) return 'q6bl';
    if (picked.some(k => k.startsWith('right'))) return 'q6br';
    return null;
  }

  // After rating left-ear tinnitus, only go to right-ear rating if right was selected in q5a
  if (currentId === 'q6bl') {
    const picked6a = answers.q6a ?? [];
    return picked6a.some(k => k.startsWith('right')) ? 'q6br' : 'q6c';
  }

  // For all other questions, use the choice's declared next value
  return choiceNext ?? questions[currentId]?.nextOnContinue ?? null;
}

function Questionnaire() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('questionnaireAnswers') || '{}');
    } catch {
      return {};
    }
  });
  const [currentId, setCurrentId] = useState((Object.keys(answers).length === 0 || location.state?.step === 'questionnaire') ? 'q1' : Object.keys(answers).slice(-1)[0]);
  const [history, setHistory] = useState((Object.keys(answers).length === 0 || location.state?.step === 'questionnaire') ? [] : Object.keys(answers).slice(0, -1));
  const [saving, setSaving] = useState(false);

  const current = questions[currentId];

  if (!current) {
    return (
      <Container maxWidth="xs" className="questionnaire__complete">
        <Typography variant="h6" align="center">{t('questionnaire.complete')}</Typography>
      </Container>
    );
  }

  // Persists answers to sessionStorage then navigates to the test flow
  const finalize = (finalAnswers) => {
    setSaving(true);
    try {
      sessionStorage.setItem('questionnaireAnswers', JSON.stringify(finalAnswers));
      navigate('/testFlow', { state: { from: location.pathname, step: 'test' } });
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  // Advance to the next question or finalize if there is none
  const advance = (nextId, updatedAnswers) => {
    setHistory(h => [...h, currentId]);
    setCurrentId(nextId);
    if (!nextId) {
      // In some cases, a question can be answered but then the user may navigate back and choose a selection that would lead to that question not being selected.
      // This filter makes sure that any answers to questions that should not have been answered are not included in the stored answers.
      const finalAnwsers = Object.fromEntries(Object.entries(updatedAnswers).filter(([questionId, value]) => [...history, currentId].includes(questionId)));
      finalize(finalAnwsers);
    }
  };

  // Used by single-select questions — records the answer and immediately advances
  const handleSelectAndAdvance = (choice) => {
    const updatedAnswers = { ...answers, [currentId]: choice.key };
    setAnswers(updatedAnswers);
    advance(resolveNext(currentId, choice.next, updatedAnswers), updatedAnswers);
  };

  // Used by multi-select questions — called when the user clicks "Continue"
  const handleContinue = () => {
    const nextId = resolveNext(currentId, null, answers);
    advance(nextId, answers);
  };

  const handlePrevious = () => {
    if (history.length !== 0) {
      setHistory(h => {
        if (!h.length) return h;
        const prev = h[h.length - 1];
        setCurrentId(prev);
        return h.slice(0, -1);
      });
    } else {
      navigate('/testFlow', { state: { from: location.pathname, step: 'questionnaire' } });
    }
  };

  const handleCheckboxChange = (key, checked) => {
    const base = Array.isArray(answers[currentId]) ? answers[currentId] : [];
    const next = checked ? [...base, key] : base.filter(k => k !== key);
    setAnswers({ ...answers, [currentId]: next });
  };

  const selectedKeys = Array.isArray(answers[currentId]) ? answers[currentId] : [];

  return (
    <div className="questionnaire">
      <Container
        maxWidth="xs"
        className="questionnaire__card"
      >
        <Typography variant="h6" gutterBottom align="center">
          {t(`questionnaire.text.${currentId}`, { defaultValue: current.text })}
        </Typography>

        {current.multiple ? (
          <>
            <FormGroup className="questionnaire__group">
              {current.choices.map(choice => (
                <FormControlLabel
                  key={choice.key}
                  control={
                    <Checkbox
                      checked={selectedKeys.includes(choice.key)}
                      onChange={e => handleCheckboxChange(choice.key, e.target.checked)}
                    />
                  }
                  label={t(`questionnaire.choices.${currentId}.${choice.key}`, { defaultValue: choice.label })}
                />
              ))}
            </FormGroup>
            <Stack direction="row" spacing={2} className="questionnaire__actions">
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={saving}
                className="questionnaire__backButton"
              >
                {t('common.back')}
              </Button>
              <Button
                className="questionnaire__continue"
                variant="contained"
                fullWidth
                disabled={!Array.isArray(answers[currentId]) || answers[currentId].length === 0}
                onClick={handleContinue}
              >
                {t('questionnaire.continue')}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Stack spacing={2} className="questionnaire__stack">
              {current.choices.map(choice => {
                const isSelected = answers[currentId] === choice.key;
                return (
                  <ButtonBase
                    key={`${currentId}-${choice.key}`}
                    onClick={() => handleSelectAndAdvance(choice)}
                    focusRipple
                    className="questionnaire__choiceBase"
                    aria-pressed={isSelected}
                    disabled={saving}
                  >
                    <Paper
                      elevation={isSelected ? 6 : 1}
                      className={`questionnaire__choiceCard ${isSelected ? 'is-selected' : ''}`}
                    >
                      <Typography className="questionnaire__choiceText">
                        {t(`questionnaire.choices.${currentId}.${choice.key}`, { defaultValue: choice.label })}
                      </Typography>
                    </Paper>
                  </ButtonBase>
                );
              })}
            </Stack>
            <Button
              className="questionnaire__backFull"
              variant="outlined"
              onClick={handlePrevious}
              disabled={saving}
              fullWidth
            >
              {t('common.back')}
            </Button>
          </>
        )}
      </Container>
    </div>
  );
}

export default Questionnaire;